/**
 * customDomain — זיהוי צד-לקוח של "דומיין עמותה" (amuta.org.il) לעומת
 * הפלטפורמה (amutech.co.il / localhost), ושליפת site-config מהשרת.
 *
 * עקרון בטיחות: fail-open. כל host של הפלטפורמה, כל שגיאת רשת, וכל תשובה
 * לא צפויה → ההתנהגות הקיימת בדיוק. רק תשובת שרת מפורשת { custom: true }
 * מפעילה מצב דומיין-לקוח — השרת הוא הסמכות, לא הניחוש המקומי.
 */

export interface SiteConfig {
  custom: boolean;
  hostname?: string;
  orgId?: string;
  orgName?: string;
  root?: { type: 'campaign' | 'tool' | 'redirect' | 'none'; slug?: string; url?: string };
}

const PLATFORM: SiteConfig = { custom: false };

/** true = הפלטפורמה שלנו; לעולם לא מצב דומיין-לקוח. */
export function isPlatformHost(hostname: string): boolean {
  const h = (hostname || '').toLowerCase();
  if (!h) return true;
  if (h === 'amutech.co.il' || h.endsWith('.amutech.co.il')) return true;
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1' || h.endsWith('.localhost')) return true;
  if (h.endsWith('.cloudfront.net')) return true;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return true; // IP literal
  return false;
}

/** האם הדפדפן כרגע על דומיין-לקוח פוטנציאלי (לפני אישור השרת). */
export function isCustomHost(): boolean {
  return !isPlatformHost(window.location.hostname);
}

let configPromise: Promise<SiteConfig> | null = null;

/**
 * שליפת site-config — פעם אחת לכל טעינת עמוד (memoized). על host של
 * הפלטפורמה לא יוצאת בקשה בכלל.
 */
export function getSiteConfig(): Promise<SiteConfig> {
  if (!isCustomHost()) return Promise.resolve(PLATFORM);
  if (!configPromise) {
    const base = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
    configPromise = fetch(`${base}/public/site-config`)
      .then((r) => (r.ok ? r.json() : PLATFORM))
      .then((data) => (data && data.custom === true ? (data as SiteConfig) : PLATFORM))
      .catch(() => PLATFORM);
  }
  return configPromise;
}
